import React, { useEffect } from 'react';

/* ─── Types ─────────────────────────────────────────── */
interface Particle {
  x: number;
  y: number;
  color: string;
  radius: number;
  vx: number;
  vy: number;
  alpha: number;
  update: () => boolean;
  draw: (ctx: CanvasRenderingContext2D) => void;
}

interface Firework {
  particles: Particle[];
  timestamp: number;
}

/* ─── Props ─────────────────────────────────────────── */
interface FixedColorFireworksProps {
  /** When true, suppresses the solid yellow backdrop. */
  hideBackground?: boolean;
  /** When true, uses blue and purple colors instead of gold */
  useBlueAndPurple?: boolean;
}

/* ─── Component ─────────────────────────────────────── */
const FixedColorFireworks: React.FC<FixedColorFireworksProps> = ({
  hideBackground = false,
  useBlueAndPurple = false,
}) => {
  useEffect(() => {
    /* ================== canvas creation ================== */
    // Backdrop canvas (optional).
    let bgCanvas: HTMLCanvasElement | null = null;
    if (!hideBackground) {
      bgCanvas = document.createElement('canvas');
      bgCanvas.style.cssText =
        'position:absolute;top:0;left:0;width:100%;height:100%;z-index:0;border:none;pointer-events:none;';
    }

    // Particle canvas (always).
    const particleCanvas = document.createElement('canvas');
    particleCanvas.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;z-index:1;border:none;pointer-events:none;';

    /* ================== insert into DOM ================== */
    const container = document.querySelector('.synthwave-landing-container');
    if (!container) {
      console.error('Container .synthwave-landing-container not found');
      return;
    }

    if (bgCanvas) {
      container.insertBefore(bgCanvas, container.firstChild);
      container.insertBefore(particleCanvas, bgCanvas.nextSibling);
    } else {
      container.insertBefore(particleCanvas, container.firstChild);
    }

    /* ================== sizing ================== */
    const resizeCanvases = () => {
      if (bgCanvas) {
        bgCanvas.width = window.innerWidth;
        bgCanvas.height = window.innerHeight;
      }
      particleCanvas.width = window.innerWidth;
      particleCanvas.height = window.innerHeight;
    };
    resizeCanvases();

    /* ================== backdrop paint ================== */
    if (bgCanvas) {
      const bgCtx = bgCanvas.getContext('2d');
      if (bgCtx) {
        bgCtx.fillStyle = '#ffee6d';
        bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
      }
    }

    /* ================== particle logic =================== */
    const particleCtx = particleCanvas.getContext('2d');
    if (!particleCtx) {
      console.error('Could not get particle canvas context');
      return;
    }

    const fireworks: Firework[] = [];

    class ParticleImpl implements Particle {
      x: number;
      y: number;
      color: string;
      radius: number;
      vx: number;
      vy: number;
      alpha: number;

      constructor(x: number, y: number, color: string) {
        this.x = x;
        this.y = y;
        this.color = color;
        this.radius = Math.random() * 2 + 1;
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 5 + 1;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.alpha = 1;
      }

      update(): boolean {
        this.x += this.vx;
        this.y += this.vy;
        this.vy += 0.05;          // gravity
        this.alpha -= 0.01;       // fade
        return this.alpha > 0;
      }

      draw(ctx: CanvasRenderingContext2D): void {
        ctx.globalAlpha = this.alpha;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const createFirework = (x: number, y: number): void => {
      const particles: Particle[] = [];
      
      // Generate blue and purple particles if requested, otherwise gold
      if (useBlueAndPurple) {
        // Random selection between blue and purple
        const colorOptions = [
          // Blue spectrum (lighter to deeper)
          'rgba(100, 149, 237, 0.8)',  // Cornflower blue
          'rgba(65, 105, 225, 0.8)',   // Royal blue
          'rgba(0, 191, 255, 0.8)',    // Deep sky blue
          'rgba(30, 144, 255, 0.8)',   // Dodger blue
          // Purple spectrum
          'rgba(186, 85, 211, 0.8)',   // Medium orchid
          'rgba(147, 112, 219, 0.8)',  // Medium purple
          'rgba(138, 43, 226, 0.8)',   // Blue violet
          'rgba(148, 0, 211, 0.8)',    // Dark violet
        ];
        
        for (let i = 0; i < 100; i++) {
          // Randomly select a color from the options
          const color = colorOptions[Math.floor(Math.random() * colorOptions.length)];
          particles.push(new ParticleImpl(x, y, color));
        }
      } else {
        // Original gold color
        const hue = Math.random() * 20 + 40; // gold-ish hue
        const color = `hsl(${hue}, 100%, 60%)`;
        for (let i = 0; i < 100; i++) {
          particles.push(new ParticleImpl(x, y, color));
        }
      }
      fireworks.push({ particles, timestamp: Date.now() });
    };

    // initial fireworks
    for (let i = 0; i < 3; i++) {
      createFirework(
        Math.random() * particleCanvas.width,
        Math.random() * particleCanvas.height * 0.5,
      );
    }

    /* ================== animation loop =================== */
    const animate = () => {
      // Clear the canvas with transparency
      particleCtx.clearRect(0, 0, particleCanvas.width, particleCanvas.height);
      
      // semi-transparent fill for trails
      particleCtx.globalCompositeOperation = 'destination-out';
      particleCtx.fillStyle = 'rgba(0,0,0,0.05)'; // Lower opacity to see background better
      particleCtx.fillRect(0, 0, particleCanvas.width, particleCanvas.height);

      // additive blend
      particleCtx.globalCompositeOperation = 'lighter'; // 'lighter' looks better with background images

      for (let i = fireworks.length - 1; i >= 0; i--) {
        const fw = fireworks[i];
        let alive = false;
        for (let j = fw.particles.length - 1; j >= 0; j--) {
          const p = fw.particles[j];
          if (p.update()) {
            p.draw(particleCtx);
            alive = true;
          } else {
            fw.particles.splice(j, 1);
          }
        }
        if (!alive && fw.particles.length === 0) {
          fireworks.splice(i, 1);
        }
      }

      // occasional new firework
      if (Math.random() < 0.02 && fireworks.length < 5) {
        createFirework(
          Math.random() * particleCanvas.width,
          Math.random() * particleCanvas.height * 0.5,
        );
      }

      requestAnimationFrame(animate);
    };
    animate();

    /* ================== resize handler =================== */
    const handleResize = () => {
      resizeCanvases();
      if (bgCanvas) {
        const bgCtx = bgCanvas.getContext('2d');
        if (bgCtx) {
          bgCtx.fillStyle = '#ffee6d';
          bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
        }
      }
    };
    window.addEventListener('resize', handleResize);

    /* ================== cleanup ========================== */
    return () => {
      window.removeEventListener('resize', handleResize);
      if (bgCanvas && container.contains(bgCanvas)) container.removeChild(bgCanvas);
      if (container.contains(particleCanvas)) container.removeChild(particleCanvas);
    };
  }, [hideBackground, useBlueAndPurple]);

  return null;
};

export default FixedColorFireworks;